import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { isEmpty } from '../utils/validationUtil';

import '../Components/TaxonomyDictionary.css';
import { FragmentApiProvider, useFragmentApi } from '../Services/FragmentApiProvider';
import FragmentParameters from './FragmentParameters';

function FragmentDetail({ canEdit }) {
  const { organizationId, fragmentId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const { getFragmentById, createFragment, updateFragment } = useFragmentApi();

  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [name, setName] = useState('');
  const [template, setTemplate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [parameters, setParameters] = useState([]);
  const [fragmentType, setFragmentType] = useState('');
  const [renderMode, setRenderMode] = useState('message');
  const [contentType, setContentType] = useState('text/html');

  const isParameterized = fragmentType === 'parameterized';
  const usesContentType = renderMode === 'attachment' || renderMode === 'control';

  useEffect(() => {
    const fetchFragment = async () => {
      setLoading(true);
      try {
        if (fragmentId !== 'new-fragment') {
          const response = await getFragmentById(fragmentId);
          setName(response.name);
          setTemplate(response.template || '');
          setIsActive(response.is_active);
          setParameters(response.parameters || []);
          setFragmentType(response.fragment_type || 'default');
          setRenderMode(response.render_mode || 'message');
          setContentType(response.content_type || 'text/html');
          setIsNew(false);
          setIsDisabled(!canEdit);
        } else {
          setIsNew(true);
          setIsDisabled(!canEdit);
        }
      } catch {
        showAlert('fail', 'Failed to load fragment');
      } finally {
        setLoading(false);
      }
    };

    fetchFragment();
  }, [fragmentId, canEdit]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/fragments`);
  };

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter a name');
      return;
    }

    if (isEmpty(fragmentType)) {
      showAlert('fail', 'Please select a fragment type');
      return;
    }

    const hasEmptyParameterFields = parameters.some(
      (parameter) => !parameter.name?.trim() || !parameter.description?.trim(),
    );
    if (isParameterized && hasEmptyParameterFields) {
      showAlert('fail', 'Please ensure all variables have both name and description filled.');
      return;
    }

    const hasEmptyNestedFieldName = (fields) =>
      fields.some(
        (field) =>
          !field.name?.trim() ||
          hasEmptyNestedFieldName(field.properties || []) ||
          hasEmptyNestedFieldName(field.items?.properties || []),
      );
    const hasEmptyFieldName = parameters.some(
      (parameter) =>
        hasEmptyNestedFieldName(parameter.properties || []) ||
        hasEmptyNestedFieldName(parameter.items?.properties || []),
    );
    if (isParameterized && hasEmptyFieldName) {
      showAlert('fail', 'Please ensure all object fields have a name.');
      return;
    }

    const data = {
      name: name,
      template: template,
      is_active: isActive,
      fragment_type: fragmentType,
      render_mode: renderMode,
      content_type: contentType,
      parameters: parameters.map(
        ({ key, name: parameterName, description, type, items, properties }) => ({
          key,
          name: parameterName,
          description,
          type: type || 'str',
          items,
          properties,
        }),
      ),
    };

    setLoading(true);
    try {
      if (isNew) {
        await createFragment(data);
        showAlert('success', 'Fragment created!');
        navigate(`/organizations/${organizationId}/fragments`);
      } else {
        await updateFragment(fragmentId, data);
        showAlert('success', 'Fragment updated!');
      }
    } catch {
      showAlert('fail', 'Failed to save Fragment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{name}</h2>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Fragment name</div>
              <div className="small-font">Enter fragment name</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="fragment-name"
                label="Fragment Name"
                placeholder="Enter name"
                type="text"
                value={name}
                onChange={(id, value) => setName(value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Fragment template</div>
              <div className="small-font">Enter template</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="template"
                label="Template"
                name="template"
                type="text"
                addedText={template}
                onChange={(value) => setTemplate(value)}
                resizable={false}
                disabled={isDisabled}
                height="200px"
              />
            </div>
          </div>
          <div className="input-row input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Fragment type</div>
              <div className="small-font">
                A parameterized fragment exposes variables the agent fills in
              </div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="fragment-type"
                label="Fragment Type"
                options={[
                  { value: 'default', label: 'Default' },
                  { value: 'parameterized', label: 'Parameterized' },
                ]}
                value={fragmentType}
                onChange={(value) => setFragmentType(value)}
                multiSelect={false}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-row input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Render mode</div>
              <div className="small-font">Select how the fragment is rendered</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="fragment-render-mode"
                label="Render Mode"
                options={[
                  { value: 'message', label: 'Message part' },
                  { value: 'attachment', label: 'Attachment' },
                  { value: 'control', label: 'Control' },
                ]}
                value={renderMode}
                onChange={(value) => setRenderMode(value)}
                multiSelect={false}
                disabled={isDisabled}
              />
            </div>
          </div>

          {usesContentType && (
            <div className="input-row input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Generated format</div>
                <div className="small-font">Format the fragment content is generated in</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id="fragment-content-type"
                  label="Generated Format"
                  options={[
                    { value: 'text/html', label: 'HTML' },
                    { value: 'application/json', label: 'JSON' },
                  ]}
                  value={contentType}
                  onChange={(value) => setContentType(value)}
                  multiSelect={false}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          <div className="input-row input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Activate fragment</div>
              <div className="small-font">Set fragment to be active or inactive</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="toggle-status"
                label={isActive ? 'Active' : 'Inactive'}
                checked={isActive}
                onChange={(id, value) => setIsActive(value)}
                disabled={false}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>

        {isParameterized && (
          <div className="row mt-4">
            <div className="col-6 radius-yes p-2 bg-yes">
              <FragmentParameters
                parameters={parameters}
                setParameters={setParameters}
                isDisabled={isDisabled}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FragmentDetailWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <FragmentApiProvider>
        <FragmentDetail canEdit={canEdit} />
      </FragmentApiProvider>
    </ConfirmationProvider>
  );
}

export default FragmentDetailWrapper;
